import type { SiteDocument } from '../../types/siteRag';
import {
  demonstrationDisclosureV1Schema,
  demonstrationDocumentProvenanceV1Schema,
  frozenDemonstrationBasisV1Schema,
} from '../../types/demonstration';
import { sha256Hex } from '../reportGeneration/reportContractHash';

export interface ModeledGridImportMargin {
  refId: number;
  phase: number;
  allocatedImportMw: number;
  facilityLoadMw: number;
  marginMw: number;
}

/** This role uses the freshly frozen full source, never a SED preview, model
 * summary, filename, report-written stamp or a utility capacity fact. */
export interface FrozenGridPlanningSources {
  basis: unknown;
  documents: readonly Pick<SiteDocument, 'id' | 'fullExtractedText' | 'contentHash' | 'demonstrationProvenance'>[];
}

const NUMBER = '((?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?)';
const amount = (text: string): number => Number(text.replace(/,/g, ''));

/** A closed affirmative table, not a search for an inner numeric substring.
 * Unknown, denied or conditional prose declines this optional source role.
 * Other rows are recognized only as boundaries; their values are not admitted. */
function normalCaseTableRows(table: string): Array<Omit<ModeledGridImportMargin, 'refId'>> | undefined {
  const header = /^\s*Case\s+Available\s+basis\s+Demand\s+Result\s*\/\s*limit\s+/i.exec(table);
  if (!header) return undefined;
  let rest = table.slice(header[0].length).trim();
  const normal = new RegExp(`^Phase\\s+([1-9]\\d*)\\s+normal\\s+${NUMBER}\\s*MW\\s+allocated\\s+import\\s+${NUMBER}\\s*MW\\s+facility\\s+${NUMBER}\\s*MW\\s+headroom\\b`, 'i');
  const otherCases = [
    new RegExp(`^Phase\\s+[1-9]\\d*\\s+one\\s+transformer\\s+out\\s+${NUMBER}\\s*x\\s*${NUMBER}\\s*MVA\\s*x\\s*${NUMBER}\\s*=\\s*${NUMBER}\\s*MW\\s+equivalent\\s+${NUMBER}\\s*MW\\s+facility\\s+Utility\\s+import\\s+still\\s+capped\\s+at\\s+${NUMBER}\\s*MW\\b`, 'i'),
    new RegExp(`^Grid\\s+outage\\s*\\+\\s*one\\s+genset\\s+out\\s+${NUMBER}\\s*x\\s*${NUMBER}\\s*MW\\s+site-net\\s*=\\s*${NUMBER}\\s*MW\\s+standby\\s+${NUMBER}\\s*MW\\s+facility\\s+${NUMBER}\\s*MW\\s+static\\s+margin;\\s*dynamics\\s+untested\\b`, 'i'),
    new RegExp(`^Cooling\\s+one\\s+unit\\s+out\\s+${NUMBER}\\s*x\\s*${NUMBER}\\s*MW\\s*=\\s*${NUMBER}\\s*MW\\s+thermal\\s+${NUMBER}\\s*MW\\s+thermal\\s+${NUMBER}\\s*MW\\s+thermal\\s+margin;\\s*curves\\s+unvalidated\\b`, 'i'),
  ];
  const rows: Array<Omit<ModeledGridImportMargin, 'refId'>> = [];
  while (rest) {
    const row = normal.exec(rest);
    if (row) {
      rows.push({ phase: Number(row[1]), allocatedImportMw: amount(row[2]!),
        facilityLoadMw: amount(row[3]!), marginMw: amount(row[4]!) });
      rest = rest.slice(row[0].length).trim();
      continue;
    }
    const other = otherCases.map(pattern => pattern.exec(rest)).find(Boolean);
    if (!other) return undefined;
    rest = rest.slice(other[0].length).trim();
  }
  return rows;
}

export function buildModeledGridImportMargins(args: {
  references?: readonly { field?: string; documentId?: string; url?: string; demonstrationDisclosure?: unknown }[];
  sources?: FrozenGridPlanningSources;
}): ModeledGridImportMargin[] {
  if (!args.sources) return [];
  const basis = frozenDemonstrationBasisV1Schema.safeParse(args.sources.basis);
  if (!basis.success || basis.data.scope !== 'sed') return [];
  const out: ModeledGridImportMargin[] = [];
  for (const [index, reference] of (args.references ?? []).entries()) {
    if (reference.field !== 'documents' || !reference.documentId || reference.url !== `doc://${reference.documentId}`) continue;
    // The ordinary reference binder preserves uploaded source labels without
    // publishing a scenario stamp. Exact authority is checked below against
    // the private frozen manifest, document provenance and both source hashes.
    // A retained public stamp must still be valid and match that private basis.
    if (reference.demonstrationDisclosure !== undefined) {
      const disclosure = demonstrationDisclosureV1Schema.safeParse(reference.demonstrationDisclosure);
      if (!disclosure.success || disclosure.data.scenarioId !== basis.data.scenarioId) continue;
    }
    const selected = args.sources.documents.filter(row => row.id === reference.documentId);
    const document = selected.length === 1 ? selected[0] : undefined;
    const manifest = basis.data.documents.find(row => row.documentId === reference.documentId);
    if (!document || !manifest || manifest.sourceKind !== 'fictional-scenario'
      || !document.fullExtractedText || !manifest.originalFileSha256) continue;
    const provenance = demonstrationDocumentProvenanceV1Schema.safeParse(document.demonstrationProvenance);
    if (!provenance.success || provenance.data.scenarioId !== basis.data.scenarioId
      || provenance.data.bodySha256 !== manifest.bodySha256
      || provenance.data.originalFileSha256 !== manifest.originalFileSha256
      || document.contentHash !== manifest.originalFileSha256
      || sha256Hex(document.fullExtractedText) !== manifest.bodySha256) continue;
    // The admitted extraction has numbered standalone section headings.
    // A heading embedded in a denied/conditional sentence is not a table.
    // Unsupported formatting declines the role instead of borrowing an inner
    // heading substring or an unrelated later "Source boundaries" paragraph.
    const section = /(?:^|[.!?](?:[ \t]{2,}|[ \t]*\r?\n[ \t]*))([1-9]\d*)\.([1-9]\d*)\s+Capacity\s+adequacy\s+by\s+operating\s+case\b([\s\S]*?)\s+([1-9]\d*)\.([1-9]\d*)\s+Source\s+boundaries\b/i.exec(document.fullExtractedText);
    if (!section || section[1] !== section[4]
      || Number(section[5]) !== Number(section[2]) + 1) continue;
    const table = section[3]!;
    const parsed = normalCaseTableRows(table);
    if (!parsed) continue;
    const rows = parsed.map(row => ({ ...row, refId: index + 1 }));
    for (const row of rows) {
      if (!Number.isSafeInteger(row.phase) || ![row.allocatedImportMw, row.facilityLoadMw, row.marginMw].every(Number.isFinite)
        || row.allocatedImportMw < row.facilityLoadMw || row.facilityLoadMw < 0
        || row.marginMw !== row.allocatedImportMw - row.facilityLoadMw) continue;
      const samePhase = rows.filter(other => other.phase === row.phase);
      if (samePhase.some(other => other.allocatedImportMw !== row.allocatedImportMw
        || other.facilityLoadMw !== row.facilityLoadMw || other.marginMw !== row.marginMw)) continue;
      if (!out.some(other => other.refId === row.refId && other.phase === row.phase)) out.push(row);
    }
  }
  return out;
}

/** An attempted grid-import margin claim is fail-closed even when its number
 * would otherwise fall through as an ungoverned contextual MW. Only the exact
 * role-bound tuple is separate from CFS; nothing populates utility headroom. */
export function checkModeledGridImportMargin(args: {
  text: string; index: number; raw: string; value: number; unit: string; rangeLow?: number;
  evidence?: readonly ModeledGridImportMargin[];
}): { supported: boolean; expected: string } | undefined {
  if (args.unit !== 'MW') return undefined;
  const lineStart = args.text.lastIndexOf('\n', args.index - 1) + 1;
  const lineEnd = args.text.indexOf('\n', args.index);
  const line = args.text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
  const relative = args.index - lineStart;
  const before = line.slice(0, relative);
  const prior = Math.max(before.lastIndexOf('. ') + 2, before.lastIndexOf(';') + 1);
  const after = line.slice(relative + args.raw.length);
  const next = after.search(/[.!?](?=\s|$)|;/);
  const end = next < 0 ? line.length : relative + args.raw.length + next;
  const start = before.lastIndexOf('. ') < 0 ? before.lastIndexOf(';') + 1 : prior;
  const clause = line.slice(start, end);
  if (!/\bgrid[- ]import\s+margin\b/i.test(clause)) return undefined;
  const expected = 'An exact numbered, frozen fictional-source reference and complete same-phase normal-case allocated-import/facility-load/modeled-margin tuple; utility service and headroom remain independently unverified.';
  const failed = { supported: false, expected };
  // Accept the producer's exact affirmative role sentence, optionally with a
  // flattened field caption. An inner `4 MW` cannot hide an expression such
  // as `2 × 4 MW`, a sign/comparator/range, or a conditional/denied predicate.
  const affirmative = new RegExp(`^\\s*(?:-\\s*[A-Za-z][A-Za-z ]{0,63}:\\s*)?In\\s+the\\s+(?:fictional|synthetic)\\s+(?:planning\\s+)?model,\\s+Phase\\s+[1-9]\\d*\\s+normal(?:[- ]case)?\\s+records\\s+${NUMBER}\\s*MW\\s+allocated(?:\\s+grid)?\\s+import,\\s+${NUMBER}\\s*MW\\s+facility\\s+load\\s+and\\s+(?:a\\s+)?${NUMBER}\\s*MW\\s+model(?:ed|led)\\s+normal[- ]case\\s+grid[- ]import\\s+margin\\s+\\[Ref\\s+[1-9]\\d*\\]\\s*$`, 'i');
  if (!affirmative.test(clause)) return failed;
  if (args.rangeLow != null && args.rangeLow !== args.value
    || !/\b(?:fictional|synthetic)\s+(?:planning\s+)?(?:model|scenario|demonstration)\b/i.test(clause)
    || !/\bmodel(?:ed|led)\b/i.test(clause)
    || /\b(?:not|no|without|invalid|rejected|withdrawn|superseded)\b/i.test(clause)
    || /\b(?:utility|ATC|hosting|headroom|firm|available|committed|secured|guaranteed|approved|verified|confirmed|delivered|operational|energized|energised)\b/i.test(clause)) return failed;
  const phases = [...clause.matchAll(/\bPhase\s+([1-9]\d*)\s+normal(?:[- ]case)?\b/gi)];
  if (phases.length !== 1 || [...clause.matchAll(/\bPhase\s+([1-9]\d*)\b/gi)].length !== 1) return failed;
  const roles = [
    new RegExp(`${NUMBER}\\s*MW\\s+(?:(?:modeled|modelled|planning|synthetic)\\s+)?allocated(?:\\s+grid)?\\s+import\\b`, 'gi'),
    new RegExp(`${NUMBER}\\s*MW\\s+facility(?:[- ]load|\\s+load)?\\b`, 'gi'),
    new RegExp(`${NUMBER}\\s*MW\\s+(?:(?:modeled|modelled|normal(?:[- ]case)?|planning)\\s+){0,4}grid[- ]import\\s+margin\\b`, 'gi'),
  ].map(pattern => [...clause.matchAll(pattern)]);
  if (roles.some(matches => matches.length !== 1)) return failed;
  const [importMatch, facilityMatch, marginMatch] = roles.map(matches => matches[0]!);
  const values = [importMatch!, facilityMatch!, marginMatch!].map(match => amount(match[1]!));
  const ownRole = [importMatch!, facilityMatch!, marginMatch!].some(match => {
    const roleStart = start + match.index!;
    return relative === roleStart && args.value === amount(match[1]!);
  });
  // Only complete plain numbered tokens bind here; a filename prefix or an
  // adjacent sentence's reference is never interpreted as a reference index.
  const refIds = [...clause.matchAll(/\[Ref\s+([1-9]\d*)\]/gi)].map(match => Number(match[1]));
  if (refIds.length !== 1 || [...clause.matchAll(/\[Ref\b[^\]]*\]/gi)].length !== 1) return failed;
  const matching = ownRole && args.evidence?.some(row => refIds.includes(row.refId)
    && row.phase === Number(phases[0]![1]) && row.allocatedImportMw === values[0]
    && row.facilityLoadMw === values[1] && row.marginMw === values[2]);
  return { supported: Boolean(matching), expected };
}
